import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Loader2, User, Mail, Lock, Shield, CheckCircle, AlertCircle } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL || "";

const getRoleBadge = (role) => {
  switch (role) {
    case "owner":
      return <Badge className="bg-purple-600 hover:bg-purple-700">Owner</Badge>;
    case "admin":
      return <Badge className="bg-blue-600 hover:bg-blue-700">Admin</Badge>;
    default:
      return <Badge variant="secondary">Benutzer</Badge>;
  }
};

export const ProfilePage = ({ user, onUserUpdate }) => {
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Form states
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSavingEmail, setIsSavingEmail] = useState("");

  // Check if user can change email (super-admin or owner)
  const canChangeEmail = profile?.is_super_admin || profile?.role === "owner";

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/api/profile`);
      setProfile(response.data);
      // Split name into first and last name
      const nameParts = (response.data.name || "").split(" ");
      setFirstName(nameParts[0] || "");
      setLastName(nameParts.slice(1).join(" ") || "");
      setEmail(response.data.email || "");
    } catch (err) {
      setError("Profil konnte nicht geladen werden");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateName = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setIsSaving(true);

    const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();

    if (!firstName.trim()) {
      setError("Vorname ist erforderlich");
      setIsSaving(false);
      return;
    }

    try {
      const response = await axios.put(`${API}/api/profile`, { name: fullName });
      setProfile({ ...profile, name: fullName });
      setSuccess("Name erfolgreich aktualisiert");
      toast.success("Name aktualisiert");
      
      if (onUserUpdate) {
        onUserUpdate({ ...user, name: fullName });
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Fehler beim Aktualisieren");
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setIsSavingEmail(true);

    const newEmail = email.trim().toLowerCase();

    if (!newEmail) {
      setError("E-Mail ist erforderlich");
      setIsSavingEmail(false);
      return;
    }

    // Basic email validation
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(newEmail)) {
      setError("Ungültiges E-Mail-Format");
      setIsSavingEmail(false);
      return;
    }

    try {
      const response = await axios.put(`${API}/api/profile`, { email: newEmail });
      setProfile({ ...profile, email: newEmail });
      setSuccess("E-Mail erfolgreich aktualisiert");
      toast.success("E-Mail aktualisiert");
      
      if (onUserUpdate) {
        onUserUpdate({ ...user, email: newEmail });
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Fehler beim Aktualisieren der E-Mail");
    } finally {
      setIsSavingEmail(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (newPassword !== confirmPassword) {
      setError("Die Passwörter stimmen nicht überein");
      return;
    }

    if (newPassword.length < 8) {
      setError("Das neue Passwort muss mindestens 8 Zeichen haben");
      return;
    }

    setIsSaving(true);

    try {
      await axios.put(`${API}/api/profile`, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setSuccess("Passwort erfolgreich geändert");
      toast.success("Passwort geändert");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setError(err.response?.data?.detail || "Fehler beim Ändern des Passworts");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
          <User className="w-8 h-8 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Mein Profil</h1>
          <p className="text-muted-foreground">Verwalten Sie Ihre Kontodaten</p>
        </div>
      </div>

      {/* Account Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Kontoinformationen
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Email</span>
            </div>
            <span className="font-medium">{profile?.email}</span>
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Rolle</span>
            </div>
            {getRoleBadge(profile?.role)}
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Status</span>
            </div>
            {profile?.is_active ? (
              <Badge variant="outline" className="text-green-600 border-green-600">Aktiv</Badge>
            ) : (
              <Badge variant="outline" className="text-red-600 border-red-600">Inaktiv</Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Update Email Card - Only for Super-Admin or Owner */}
      {canChangeEmail && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              E-Mail ändern
            </CardTitle>
            <CardDescription>
              Aktualisieren Sie Ihre E-Mail-Adresse (nur für Super-Admin/Owner)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpdateEmail} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Neue E-Mail-Adresse</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ihre@email.de"
                  data-testid="profile-email-input"
                />
              </div>
              <Button type="submit" disabled={isSavingEmail || email === profile?.email} data-testid="save-email-btn">
                {isSavingEmail ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Speichern...
                  </>
                ) : (
                  "E-Mail aktualisieren"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Update Name Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Name ändern
          </CardTitle>
          <CardDescription>
            Aktualisieren Sie Ihren Anzeigenamen
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleUpdateName} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="w-4 h-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {success && (
              <Alert className="border-green-500 text-green-700">
                <CheckCircle className="w-4 h-4" />
                <AlertDescription>{success}</AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">Vorname</Label>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Vorname"
                  data-testid="profile-firstname-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Nachname</Label>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Nachname"
                  data-testid="profile-lastname-input"
                />
              </div>
            </div>

            <Button type="submit" disabled={isSaving || (!firstName.trim() && !lastName.trim())} data-testid="save-name-btn">
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Speichern...
                </>
              ) : (
                "Name speichern"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Update Password Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Passwort ändern
          </CardTitle>
          <CardDescription>
            Ändern Sie Ihr Anmeldepasswort
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleUpdatePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Aktuelles Passwort</Label>
              <Input
                id="currentPassword"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="current-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="newPassword">Neues Passwort</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mindestens 8 Zeichen"
                data-testid="new-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Passwort wiederholen"
                data-testid="confirm-password-input"
              />
            </div>

            <Button 
              type="submit" 
              disabled={isSaving || !currentPassword || !newPassword || !confirmPassword}
              data-testid="change-password-btn"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Ändern...
                </>
              ) : (
                "Passwort ändern"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfilePage;
